import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { formatCurrency } from '../utils/formatters';

const API = '/api';

const Ic = ({ d, size = 16, color = 'currentColor', sw = 2 }) => (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const ic = {
    wrench: ['M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z'],
    fuel: ['M3 22V5a2 2 0 0 1 2-2h10a2 2 0 0 1 2 2v17', 'M13 2l4 4', 'M17 6h1a2 2 0 0 1 2 2v3a2 2 0 0 0 2 2'],
    tire: ['M12 12m-10 0a10 10 0 1 0 20 0 10 10 0 1 0-20 0', 'M12 12m-3 0a3 3 0 1 0 6 0 3 3 0 1 0-6 0', 'M12 2v4', 'M12 18v4', 'M2 12h4', 'M18 12h4'],
    shield: ['M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'],
    box: ['M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z'],
    chart: ['M18 20V10', 'M12 20V4', 'M6 20v-6'],
    alert: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    check: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3'],
    clock: ['M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z', 'M12 6v6l4 2'],
    arrowUp: 'M12 19V5 M5 12l7-7 7 7',
    arrowDown: 'M12 5v14 M19 12l-7 7-7-7',
    calendar: ['M16 2v4', 'M8 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z'],
    truck: ['M7 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M17 17m-2 0a2 2 0 1 0 4 0 2 2 0 1 0-4 0', 'M5 17H3V7c0-1.1.9-2 2-2h11v12h-2', 'M19 17h2v-6l-3-4h-2v7h4'],
    refresh: ['M23 4v6h-6', 'M1 20v-6h6', 'M3.51 9a9 9 0 0 1 14.85-3.36L23 10', 'M1 14l4.64 4.36A9 9 0 0 0 20.49 15'],
    plus: 'M12 5v14 M5 12h14',
    arrowRight: 'M5 12h14 M12 5l7 7-7 7',
};

/* ── Animated Ring ── */
function Ring({ pct, size = 56, thickness = 5, color = '#22c55e', bg = '#f1f5f9' }) {
    const r = (size - thickness) / 2;
    const c = 2 * Math.PI * r;
    const dash = (pct / 100) * c;
    return (
        <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={bg} strokeWidth={thickness} />
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={thickness}
                strokeDasharray={`${dash} ${c - dash}`} strokeLinecap="round" style={{ transition: 'stroke-dasharray 1s ease' }} />
        </svg>
    );
}

export default function MaintenanceOverview({ user, operatorDetails, onNavigate }) {
    const [vehicles, setVehicles] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchData(); }, [user]); // eslint-disable-line

    const fetchData = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/vehicles/${user?.operatorId || user?._id || 'all'}`);
            setVehicles(res.data);
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const m = useMemo(() => {
        const total = vehicles.length;
        const active = vehicles.filter(v => v.vehicleStatus === 'Active').length;
        const maint = vehicles.filter(v => v.vehicleStatus === 'Under Maintenance').length;
        const damaged = vehicles.filter(v => v.vehicleStatus === 'Damaged').length;
        const healthPct = total > 0 ? Math.round((active / total) * 100) : 0;
        const maintPct = total > 0 ? Math.round((maint / total) * 100) : 0;
        const totalIns = vehicles.reduce((s, v) => s + (v.insuranceMonthly || 0), 0);
        const totalOther = vehicles.reduce((s, v) => s + (v.otherCostsMonthly || 0), 0);
        const years = vehicles.filter(v => v.year).map(v => new Date().getFullYear() - v.year);
        const avgAge = years.length ? (years.reduce((a, b) => a + b, 0) / years.length).toFixed(1) : '0';

        // Simulated upcoming tasks based on vehicle data
        const upcoming = vehicles.slice(0, 4).map((v, i) => {
            const tasks = ['Oil Change', 'Brake Inspection', 'Tyre Rotation', 'Battery Check', 'Filter Replacement', 'Coolant Flush'];
            const urgency = ['overdue', 'due-soon', 'scheduled', 'upcoming'];
            return {
                id: v._id,
                vehicle: `${v.make} ${v.model}`,
                plate: v.plateNumber,
                task: tasks[i % tasks.length],
                urgency: urgency[i % urgency.length],
                daysUntil: i === 0 ? -2 : i === 1 ? 3 : i === 2 ? 7 : 15,
                color: v.color || '#e2e8f0',
            };
        });

        return { total, active, maint, damaged, healthPct, maintPct, totalIns, totalOther, avgAge, upcoming };
    }, [vehicles]);

    const urgencyStyle = {
        'overdue': { bg: '#fef2f2', color: '#dc2626', border: '#fecaca', label: 'OVERDUE', icon: '🔴' },
        'due-soon': { bg: '#fff7ed', color: '#ea580c', border: '#fed7aa', label: 'DUE SOON', icon: '🟠' },
        'scheduled': { bg: '#f0fdf4', color: '#16a34a', border: '#bbf7d0', label: 'SCHEDULED', icon: '🟢' },
        'upcoming': { bg: '#eff6ff', color: '#2563eb', border: '#bfdbfe', label: 'UPCOMING', icon: '🔵' },
    };

    return (
        <div className="mo">
            <style>{`
                .mo { font-family: 'Inter', sans-serif; min-height: 100vh; background: #f8fafc; }

                /* ── Top Gradient Banner ── */
                .mo-banner {
                    background: linear-gradient(135deg, #0c4a6e 0%, #075985 40%, #0369a1 100%);
                    padding: 28px 36px 60px; position: relative; overflow: hidden;
                }
                .mo-banner::after {
                    content: ''; position: absolute; top: -50%; right: -15%; width: 400px; height: 400px;
                    background: radial-gradient(circle, rgba(56, 189, 248, 0.12) 0%, transparent 70%);
                    border-radius: 50%;
                }
                .mo-banner-top { display: flex; justify-content: space-between; align-items: center; position: relative; z-index: 1; }
                .mo-banner h1 { font-size: 28px; font-weight: 800; color: #fff; margin: 0 0 4px; }
                .mo-banner p { font-size: 14px; color: rgba(255,255,255,0.55); margin: 0; }
                .mo-banner-btns { display: flex; gap: 10px; }
                .mo-bbtn {
                    padding: 9px 18px; border-radius: 10px; font-size: 13px; font-weight: 600;
                    cursor: pointer; display: flex; align-items: center; gap: 6px; transition: all 0.2s;
                    border: 1px solid rgba(255,255,255,0.15); background: rgba(255,255,255,0.08);
                    color: #fff; backdrop-filter: blur(8px);
                }
                .mo-bbtn:hover { background: rgba(255,255,255,0.16); }
                .mo-bbtn.green { background: #22c55e; border: none; box-shadow: 0 4px 14px rgba(34,197,94,0.35); }
                .mo-bbtn.green:hover { background: #16a34a; }

                /* ── Floating Metrics Strip ── */
                .mo-metrics {
                    display: flex; gap: 0; margin: -36px 36px 24px; position: relative; z-index: 2;
                    background: #fff; border-radius: 18px; overflow: hidden;
                    box-shadow: 0 12px 40px rgba(0,0,0,0.08); border: 1px solid rgba(0,0,0,0.04);
                }
                .mo-metric {
                    flex: 1; padding: 22px 26px; display: flex; align-items: center; gap: 16px;
                    border-right: 1px solid #f1f5f9; position: relative;
                }
                .mo-metric:last-child { border-right: none; }
                .mo-metric-ring { position: relative; flex-shrink: 0; }
                .mo-metric-ring-label {
                    position: absolute; inset: 0; display: flex; align-items: center; justify-content: center;
                    font-size: 13px; font-weight: 800; color: #0f172a;
                }
                .mo-metric-info { flex: 1; }
                .mo-metric-title { font-size: 11px; font-weight: 700; color: #94a3b8; text-transform: uppercase; letter-spacing: 0.06em; margin-bottom: 4px; }
                .mo-metric-val { font-size: 24px; font-weight: 800; color: #0f172a; line-height: 1; }
                .mo-metric-val span { font-size: 12px; color: #94a3b8; font-weight: 500; }
                .mo-metric-sub { font-size: 11px; color: #64748b; margin-top: 2px; }

                /* ── Content Area ── */
                .mo-content { padding: 0 36px 36px; }

                /* ── Two-panel Layout ── */
                .mo-panels { display: grid; grid-template-columns: 5fr 7fr; gap: 22px; margin-bottom: 22px; }

                /* ── Panel Card ── */
                .mo-panel {
                    background: #fff; border-radius: 18px; border: 1px solid rgba(0,0,0,0.04);
                    box-shadow: 0 4px 16px rgba(0,0,0,0.03); overflow: hidden;
                }
                .mo-panel-head {
                    padding: 20px 24px 16px; display: flex; justify-content: space-between; align-items: center;
                    border-bottom: 1px solid #f8fafc;
                }
                .mo-panel-title { font-size: 15px; font-weight: 700; color: #0f172a; }
                .mo-panel-badge { font-size: 10px; font-weight: 700; padding: 3px 10px; border-radius: 8px; text-transform: uppercase; letter-spacing: 0.05em; }
                .mo-panel-body { padding: 20px 24px; }

                /* ── Quick Access Tiles ── */
                .mo-tiles { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
                .mo-tile {
                    padding: 20px; border-radius: 14px; cursor: pointer; transition: all 0.25s;
                    border: 1.5px solid #f1f5f9; background: #fafbfc;
                    display: flex; flex-direction: column; align-items: center; gap: 10px; text-align: center;
                }
                .mo-tile:hover { border-color: #0c4a6e; transform: translateY(-3px); box-shadow: 0 8px 20px rgba(12,74,110,0.1); }
                .mo-tile-icon {
                    width: 48px; height: 48px; border-radius: 14px; display: flex;
                    align-items: center; justify-content: center;
                }
                .mo-tile-label { font-size: 13px; font-weight: 700; color: #1e293b; }
                .mo-tile-desc { font-size: 11px; color: #94a3b8; line-height: 1.4; }

                /* ── Maintenance Timeline ── */
                .mo-timeline { display: flex; flex-direction: column; gap: 0; }
                .mo-tl-item {
                    display: flex; align-items: stretch; gap: 16px; padding: 16px 0;
                    border-bottom: 1px solid #f8fafc; position: relative;
                }
                .mo-tl-item:last-child { border-bottom: none; }

                /* Timeline line */
                .mo-tl-line {
                    width: 32px; display: flex; flex-direction: column; align-items: center; flex-shrink: 0;
                    position: relative;
                }
                .mo-tl-dot {
                    width: 12px; height: 12px; border-radius: 50%; z-index: 1;
                    border: 3px solid; flex-shrink: 0;
                }
                .mo-tl-connector {
                    width: 2px; flex: 1; margin-top: 4px;
                }
                .mo-tl-body { flex: 1; min-width: 0; }
                .mo-tl-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
                .mo-tl-task { font-size: 14px; font-weight: 700; color: #0f172a; }
                .mo-tl-urgency {
                    font-size: 9px; font-weight: 800; padding: 2px 8px; border-radius: 6px;
                    text-transform: uppercase; letter-spacing: 0.06em;
                }
                .mo-tl-vehicle { font-size: 12px; color: #64748b; }
                .mo-tl-vehicle strong { color: #334155; }
                .mo-tl-days { font-size: 11px; color: #94a3b8; margin-top: 2px; }
                .mo-tl-color {
                    width: 6px; border-radius: 4px; flex-shrink: 0; margin-right: -8px;
                }

                /* ── Bottom Row ── */
                .mo-bottom { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 22px; }
                .mo-stat-card {
                    background: #fff; border-radius: 18px; padding: 24px;
                    border: 1px solid rgba(0,0,0,0.04); box-shadow: 0 4px 16px rgba(0,0,0,0.03);
                    display: flex; flex-direction: column; gap: 16px;
                }
                .mo-stat-head { display: flex; justify-content: space-between; align-items: center; }
                .mo-stat-title { font-size: 13px; font-weight: 700; color: #475569; }
                .mo-stat-icon {
                    width: 36px; height: 36px; border-radius: 10px; display: flex;
                    align-items: center; justify-content: center;
                }
                .mo-stat-big { font-size: 32px; font-weight: 800; color: #0f172a; line-height: 1; }
                .mo-stat-big span { font-size: 14px; color: #94a3b8; font-weight: 500; }
                .mo-stat-bar { height: 6px; border-radius: 6px; background: #f1f5f9; overflow: hidden; }
                .mo-stat-fill { height: 100%; border-radius: 6px; transition: width 0.8s ease; }
                .mo-stat-footer { font-size: 11px; color: #94a3b8; display: flex; align-items: center; gap: 4px; }

                .mo-empty { color: #94a3b8; font-size: 13px; text-align: center; padding: 40px 0; font-style: italic; }

                @media (max-width: 1100px) {
                    .mo-panels { grid-template-columns: 1fr; }
                    .mo-bottom { grid-template-columns: 1fr; }
                    .mo-metrics { flex-direction: column; }
                    .mo-metric { border-right: none; border-bottom: 1px solid #f1f5f9; }
                }
            `}</style>

            {/* ── BANNER ── */}
            <div className="mo-banner">
                <div className="mo-banner-top">
                    <div>
                        <h1>Maintenance Overview</h1>
                        <p>Fleet health monitoring, service schedules & cost tracking</p>
                    </div>
                    <div className="mo-banner-btns">
                        <button className="mo-bbtn" onClick={fetchData}>
                            <Ic d={ic.refresh} size={14} color="#fff" /> Refresh
                        </button>
                        <button className="mo-bbtn green" onClick={() => onNavigate && onNavigate('maintenance-jobs')}>
                            <Ic d={ic.plus} size={14} color="#fff" /> New Job
                        </button>
                    </div>
                </div>
            </div>

            {/* ── FLOATING METRICS ── */}
            <div className="mo-metrics">
                <div className="mo-metric">
                    <div className="mo-metric-ring">
                        <Ring pct={m.healthPct} size={56} thickness={5} color="#22c55e" />
                        <div className="mo-metric-ring-label">{m.healthPct}%</div>
                    </div>
                    <div className="mo-metric-info">
                        <div className="mo-metric-title">Fleet Health</div>
                        <div className="mo-metric-val">{m.active}<span> / {m.total}</span></div>
                        <div className="mo-metric-sub">Operational vehicles</div>
                    </div>
                </div>

                <div className="mo-metric">
                    <div className="mo-metric-ring">
                        <Ring pct={m.maintPct} size={56} thickness={5} color="#f59e0b" />
                        <div className="mo-metric-ring-label">{m.maint}</div>
                    </div>
                    <div className="mo-metric-info">
                        <div className="mo-metric-title">In Service</div>
                        <div className="mo-metric-val">{m.maint + m.damaged}</div>
                        <div className="mo-metric-sub">{m.maint} maintenance · {m.damaged} damaged</div>
                    </div>
                </div>

                <div className="mo-metric">
                    <div className="mo-metric-ring">
                        <Ring pct={Math.min(parseFloat(m.avgAge) * 10, 100)} size={56} thickness={5} color="#00B2A9" />
                        <div className="mo-metric-ring-label">{m.avgAge}</div>
                    </div>
                    <div className="mo-metric-info">
                        <div className="mo-metric-title">Avg Vehicle Age</div>
                        <div className="mo-metric-val">{m.avgAge} <span>years</span></div>
                        <div className="mo-metric-sub">Fleet average</div>
                    </div>
                </div>

                <div className="mo-metric">
                    <div className="mo-metric-ring">
                        <Ring pct={65} size={56} thickness={5} color="#ef4444" />
                        <div className="mo-metric-ring-label" style={{ fontSize: 10 }}>{operatorDetails?.defaultCurrency?.split('(')[1]?.replace(')', '') || 'USD'}</div>
                    </div>
                    <div className="mo-metric-info">
                        <div className="mo-metric-title">Monthly Maint. Cost</div>
                        <div className="mo-metric-val" style={{ fontSize: 20 }}>{formatCurrency(m.totalIns + m.totalOther, operatorDetails?.country)}</div>
                        <div className="mo-metric-sub">Insurance + service costs</div>
                    </div>
                </div>
            </div>

            {/* ── MAIN CONTENT ── */}
            <div className="mo-content">
                {/* Two-panel: Quick Access + Timeline */}
                <div className="mo-panels">
                    {/* Quick Access */}
                    <div className="mo-panel">
                        <div className="mo-panel-head">
                            <span className="mo-panel-title">Quick Access</span>
                        </div>
                        <div className="mo-panel-body">
                            <div className="mo-tiles">
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('maintenance-jobs')}>
                                    <div className="mo-tile-icon" style={{ background: '#ecfdf5' }}>
                                        <Ic d={ic.wrench} size={22} color="#059669" />
                                    </div>
                                    <span className="mo-tile-label">Maintenance Jobs</span>
                                    <span className="mo-tile-desc">Service orders & schedules</span>
                                </div>
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('fuel-management')}>
                                    <div className="mo-tile-icon" style={{ background: '#fff7ed' }}>
                                        <Ic d={ic.fuel} size={22} color="#ea580c" />
                                    </div>
                                    <span className="mo-tile-label">Fuel Tracking</span>
                                    <span className="mo-tile-desc">Consumption & refueling</span>
                                </div>
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('tyres-battery')}>
                                    <div className="mo-tile-icon" style={{ background: '#eef2ff' }}>
                                        <Ic d={ic.tire} size={22} color="#11427D" />
                                    </div>
                                    <span className="mo-tile-label">Tyres & Battery</span>
                                    <span className="mo-tile-desc">Replacements & tracking</span>
                                </div>
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('spare-parts')}>
                                    <div className="mo-tile-icon" style={{ background: '#fdf4ff' }}>
                                        <Ic d={ic.box} size={22} color="#a855f7" />
                                    </div>
                                    <span className="mo-tile-label">Spare Parts</span>
                                    <span className="mo-tile-desc">Inventory management</span>
                                </div>
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('compliance')}>
                                    <div className="mo-tile-icon" style={{ background: '#f0fdf4' }}>
                                        <Ic d={ic.shield} size={22} color="#16a34a" />
                                    </div>
                                    <span className="mo-tile-label">Compliance</span>
                                    <span className="mo-tile-desc">Regulations & inspections</span>
                                </div>
                                <div className="mo-tile" onClick={() => onNavigate && onNavigate('maintenance-reports')}>
                                    <div className="mo-tile-icon" style={{ background: '#fef2f2' }}>
                                        <Ic d={ic.chart} size={22} color="#dc2626" />
                                    </div>
                                    <span className="mo-tile-label">Reports</span>
                                    <span className="mo-tile-desc">Analytics & insights</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Maintenance Timeline */}
                    <div className="mo-panel">
                        <div className="mo-panel-head">
                            <span className="mo-panel-title">Maintenance Schedule</span>
                            <span className="mo-panel-badge" style={{ background: '#eff6ff', color: '#1d4ed8' }}>
                                {m.upcoming.length} Tasks
                            </span>
                        </div>
                        <div className="mo-panel-body">
                            {m.upcoming.length === 0 ? (
                                <div className="mo-empty">No vehicles in fleet — add vehicles to see maintenance schedules</div>
                            ) : (
                                <div className="mo-timeline">
                                    {m.upcoming.map((item, idx) => {
                                        const u = urgencyStyle[item.urgency];
                                        return (
                                            <div key={item.id} className="mo-tl-item">
                                                <div className="mo-tl-color" style={{ background: item.color }} />
                                                <div className="mo-tl-line">
                                                    <div className="mo-tl-dot" style={{ borderColor: u.color, background: u.bg }} />
                                                    {idx < m.upcoming.length - 1 && (
                                                        <div className="mo-tl-connector" style={{ background: '#e2e8f0' }} />
                                                    )}
                                                </div>
                                                <div className="mo-tl-body">
                                                    <div className="mo-tl-header">
                                                        <span className="mo-tl-task">{item.task}</span>
                                                        <span className="mo-tl-urgency" style={{ background: u.bg, color: u.color, border: `1px solid ${u.border}` }}>
                                                            {u.icon} {u.label}
                                                        </span>
                                                    </div>
                                                    <div className="mo-tl-vehicle">
                                                        <strong>{item.vehicle}</strong> · {item.plate}
                                                    </div>
                                                    <div className="mo-tl-days">
                                                        {item.daysUntil < 0
                                                            ? `${Math.abs(item.daysUntil)} days overdue`
                                                            : item.daysUntil === 0
                                                                ? 'Due today'
                                                                : `Due in ${item.daysUntil} days`
                                                        }
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Bottom Stats */}
                <div className="mo-bottom">
                    <div className="mo-stat-card">
                        <div className="mo-stat-head">
                            <span className="mo-stat-title">Fleet Availability</span>
                            <div className="mo-stat-icon" style={{ background: '#ecfdf5' }}>
                                <Ic d={ic.check} size={18} color="#059669" />
                            </div>
                        </div>
                        <div className="mo-stat-big">{m.healthPct}<span>%</span></div>
                        <div className="mo-stat-bar">
                            <div className="mo-stat-fill" style={{ width: `${m.healthPct}%`, background: '#22c55e' }} />
                        </div>
                        <div className="mo-stat-footer">
                            <Ic d={ic.arrowUp} size={12} color="#22c55e" />
                            {m.active} of {m.total} vehicles available
                        </div>
                    </div>

                    <div className="mo-stat-card">
                        <div className="mo-stat-head">
                            <span className="mo-stat-title">Insurance Cost</span>
                            <div className="mo-stat-icon" style={{ background: '#eef2ff' }}>
                                <Ic d={ic.shield} size={18} color="#11427D" />
                            </div>
                        </div>
                        <div className="mo-stat-big">{formatCurrency(m.totalIns, operatorDetails?.country)}</div>
                        <div className="mo-stat-bar">
                            <div className="mo-stat-fill" style={{
                                width: `${m.totalIns + m.totalOther > 0 ? (m.totalIns / (m.totalIns + m.totalOther)) * 100 : 0}%`,
                                background: '#00B2A9'
                            }} />
                        </div>
                        <div className="mo-stat-footer">
                            Monthly insurance premiums
                        </div>
                    </div>

                    <div className="mo-stat-card">
                        <div className="mo-stat-head">
                            <span className="mo-stat-title">Other Costs</span>
                            <div className="mo-stat-icon" style={{ background: '#fefce8' }}>
                                <Ic d={ic.wrench} size={18} color="#d97706" />
                            </div>
                        </div>
                        <div className="mo-stat-big">{formatCurrency(m.totalOther, operatorDetails?.country)}</div>
                        <div className="mo-stat-bar">
                            <div className="mo-stat-fill" style={{
                                width: `${m.totalIns + m.totalOther > 0 ? (m.totalOther / (m.totalIns + m.totalOther)) * 100 : 0}%`,
                                background: '#f59e0b'
                            }} />
                        </div>
                        <div className="mo-stat-footer">
                            Maintenance & service costs
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
